import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Building2, FileCheck, Settings, Users, ClipboardCheck, Wrench, 
  Award, ArrowRight, CheckCircle2, AlertTriangle, Cog, FileText,
  Zap, Shield, Target, ChevronDown, ChevronUp
} from 'lucide-react';
import SEO, { generateServiceSchema } from '@/components/SEO';

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
};

const PharmaEngineeringPage = () => {
  const { locale } = useParams();
  const [expandedProfile, setExpandedProfile] = useState(false);

  const problemsWeSolve = [
    'HVAC problems e.g. balancing and cleanroom failures',
    'Poor vendor coordination',
    'Escalating project costs / budget overruns',
    'Documentation gaps',
    'Shutdown planning issues & delays',
    'Compliance failures',
    'Compressed air contamination',
    'Audit risks',
    'Non-compliance observations',
    'Utility sizing errors'
  ];

  const projectTypes = [
    'Greenfield Plant Setup',
    'Brownfield Expansions / Upgrades',
    'Nutraceutical Manufacturing Setup',
    'Warehouse Projects',
    'Capacity Expansion Projects',
    'Cleanroom Projects',
    'Regulatory Upgrade Projects'
  ];

  const engagementModels = [
    {
      title: 'Feasibility Study',
      desc: 'Site assessment, capacity planning, CAPEX estimation, and risk identification to validate project viability before commitment.',
      icon: Target
    },
    {
      title: 'Concept Engineering',
      desc: 'Facility layout, cleanroom zoning, utility block diagrams, and equipment selection at the front-end engineering stage.',
      icon: Cog
    },
    {
      title: 'Full PMC',
      desc: 'End-to-end Project Management Consultancy — from concept to commissioning — as your single point of accountability.',
      icon: ClipboardCheck
    },
    {
      title: 'cGMP Gap Assessment',
      desc: 'Systematic review of facility, systems, and documentation against cGMP standards with prioritized remediation roadmap.',
      icon: Shield
    },
    {
      title: 'HVAC Audit',
      desc: 'Pressure-cascade verification, airflow visualization, AHU performance review, and energy-efficiency assessment.',
      icon: Settings
    },
    {
      title: 'Utility Optimization',
      desc: 'Compressed air, water (PW/WFI), steam, and electrical systems audit with redesign for reliability and cost reduction.',
      icon: Wrench
    },
    {
      title: 'Industrial Automation',
      desc: 'Automation strategy, PLC/SCADA integration, and instrumentation design for cGMP-compliant facilities.',
      icon: Zap
    },
    {
      title: 'Audit Compliances & Documentation',
      desc: 'Mock audits, gap assessments, remediation, and full cGMP documentation pack — DQ/IQ/OQ, SOPs, P&IDs, and as-builts.',
      icon: FileText
    }
  ];

  const industriesWeServe = [
    'Pharmaceuticals',
    'Biotechnology',
    'API Manufacturing',
    'Nutraceutical',
    'Food Processing',
    'Healthcare',
    'Cosmetics & Personal Care',
    'Veterinary Pharma'
  ];

  const services = {
    preProject: {
      title: 'Pre-Project & Feasibility Services',
      icon: Target,
      items: ['Project concept development', 'Capacity planning', 'Site assessment', 'Feasibility studies', 'Budget estimation (CAPEX)', 'Risk identification']
    },
    engineering: {
      title: 'Engineering Services',
      icon: Cog,
      items: ['Facility layout planning', 'Cleanroom design concepts', 'HVAC system design support', 'Utility system planning (Compressed Air, Water, HVAC, Electrical)', 'Equipment selection support', 'Material & personnel flow design', 'Warehouse Design']
    },
    pmc: {
      title: 'PMC Services',
      icon: ClipboardCheck,
      items: ['Project planning & scheduling', 'Vendor identification & evaluation', 'Design coordination', 'Project monitoring & control', 'Cost monitoring', 'Risk management', 'Site supervision', 'Progress reporting']
    },
    compliance: {
      title: 'Compliance & cGMP Support',
      icon: Shield,
      items: ['cGMP design review', 'Area classification', 'Pressure cascade planning', 'Documentation support', 'Validation coordination (IQ/OQ support)']
    },
    execution: {
      title: 'Execution Support',
      icon: Wrench,
      items: ['Installation supervision', 'Quality checks', 'Vendor coordination', 'Commissioning support', 'Trial runs and system stabilization']
    },
    audits: {
      title: 'Audits Support & Documentation',
      icon: FileText,
      items: ['Equipment Qualification Documents (DQ/IQ/OQ)', 'SOPs and Maintenance Schedules', 'As-built drawings & P&ID Documents', 'Mock audits and team training']
    }
  };

  const coreStrengths = [
    '25+ years of pharma engineering experience',
    'Strong technical expertise in utilities & HVAC systems',
    'Deep understanding of cGMP facilities',
    'Practical project execution experience',
    'Maintenance-oriented engineering approach',
    'Strong troubleshooting and problem-solving capability',
    'Hands-on involvement in projects'
  ];

  const approach = [
    { title: 'Practical Engineering', desc: 'We design systems that are not only compliant but also easy to operate and maintain.' },
    { title: 'Client-Centric', desc: "We act as client's representative and ensure project success." },
    { title: 'Transparent Working', desc: 'Clear communication, documented decisions, and client approvals at every stage.' },
    { title: 'Flexible Execution', desc: 'Lean core team with scalable expert support based on project requirements.' }
  ];

  const caseStudyResults = [
    { value: '18%', label: 'Project cost reduction' },
    { value: '0', label: 'Audit observations' },
    { value: '15%', label: 'Energy savings achieved' },
    { value: '100%', label: 'On-time completion' }
  ];

  const leaderProfile = {
    name: 'Nitin S. Kshirsagar',
    title: 'Pharma Engineering & Project Execution Lead',
    credentials: ['DME', '30+ Years Experience'],
    summary: 'With over 30 years of experience in pharmaceutical engineering and turnkey project execution, he brings deep technical expertise combined with strong practical understanding of plant operations.',
    experience: [
      '30+ years in pharmaceutical engineering, maintenance & turnkey project execution',
      'Extensive experience in international project execution',
      'Hands-on exposure to cGMP-compliant manufacturing facilities',
      'Strong expertise in HVAC and cleanroom systems',
      'Local and overseas audit exposure'
    ],
    expertise: [
      'Facility layout and engineering design',
      'Project execution & coordination',
      'Utility systems (Air, Water, Steam, HVAC, ETP, Electricity, Firefighting)',
      'Maintenance-oriented engineering approach',
      'cGMP engineering practices',
      'Energy management and cost optimization',
      'Vendor management'
    ],
    strengths: [
      'Practical issue identification during project execution',
      'Effective leadership and team coordination',
      'Quality-focused execution within scheduled timelines',
      'Up-to-date with latest pharma engineering technologies',
      'Audit and regulatory compliance exposure'
    ],
    tools: ['Energy Management', 'Vendor Management', 'cGMP Engineering Practices'],
    audits: ['cGMP', 'FDA', 'WHO', 'ISO 9001:2008', 'MHRA', 'USFDA']
  };

  const pharmaServiceSchema = generateServiceSchema({
    type: 'Project Management Consultancy',
    name: 'Pharma Engineering & PMC Consulting',
    description: 'End-to-end PMC services for pharmaceutical, nutraceutical, API, biotech, and healthcare manufacturers. cGMP-compliant facility expansion from concept to commissioning.'
  });

  return (
    <div className="pt-20" data-testid="pharma-engineering-page">
      <SEO 
        title="Pharma Engineering & PMC Consulting"
        description="End-to-end Project Management Consultancy (PMC) for pharmaceutical facility expansion. 25+ years expertise in cGMP-compliant design, HVAC, utilities, and regulatory compliance."
        path="/services/pharma-engineering"
        structuredData={pharmaServiceSchema}
      />

      {/* Hero Section */}
      <section className="relative py-20 md:py-28 bg-gradient-to-br from-white via-light-grey to-white overflow-hidden">
        <div className="absolute top-0 right-0 w-1/3 h-full bg-teal/5" />
        
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <span className="inline-flex items-center px-4 py-2 bg-teal/10 text-teal rounded-full text-sm font-medium mb-6">
                <Building2 className="w-4 h-4 mr-2" />
                Flagship Service
              </span>
              <h1 className="font-playfair text-4xl md:text-5xl lg:text-6xl text-navy leading-tight mb-6">
                Pharma Engineering & Project Management Consultancy (PMC)
              </h1>
              <p className="text-lg text-mid-grey leading-relaxed mb-4">
                Senior pharma engineering execution expertise with real plant-level understanding.
              </p>
              <p className="text-mid-grey leading-relaxed mb-8">
                With over 27+ years of hands-on experience in pharmaceutical engineering, utilities, HVAC systems, and project execution, we bring a unique combination of technical expertise and real plant-level understanding. Our approach is not theoretical consulting — we deliver practical, implementable, and operation-friendly engineering solutions.
              </p>
              <Link to={`/${locale}/contact`} className="btn-primary" data-testid="pharma-hero-cta">
                Discuss Your Project
              </Link>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="hidden lg:block"
            >
              <img 
                src="/images/external/photo-1587854692152-cbe660dbde88.jpg"
                alt="Pharmaceutical manufacturing facility"
                className="rounded-2xl shadow-2xl w-full h-[400px] object-cover"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Core Strengths */}
      <section className="py-16 bg-teal">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-white text-2xl font-bold text-center mb-8">Our Core Strength</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
            {coreStrengths.map((strength, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                className="bg-white/10 backdrop-blur-sm p-4 rounded-lg text-center"
              >
                <CheckCircle2 className="w-6 h-6 text-white mx-auto mb-2" />
                <p className="text-white text-xs">{strength}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Problems We Solve */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center mb-12"
          >
            <motion.span variants={fadeInUp} className="text-teal font-medium text-sm uppercase tracking-wider">
              Challenges We Address
            </motion.span>
            <motion.h2 variants={fadeInUp} className="font-playfair text-3xl md:text-4xl text-navy mt-4">
              Problems We Solve
            </motion.h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-4">
            {problemsWeSolve.map((problem, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                className="flex items-start bg-light-grey p-4 rounded-lg"
              >
                <AlertTriangle className="w-5 h-5 text-teal mr-3 flex-shrink-0 mt-0.5" />
                <span className="text-sm text-navy">{problem}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Services */}
      <section className="py-20 md:py-28 bg-light-grey">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center mb-12"
          >
            <motion.span variants={fadeInUp} className="text-teal font-medium text-sm uppercase tracking-wider">
              What We Offer
            </motion.span>
            <motion.h2 variants={fadeInUp} className="font-playfair text-3xl md:text-4xl text-navy mt-4">
              Our Services
            </motion.h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {Object.values(services).map((service, index) => {
              const IconComponent = service.icon;
              return (
                <motion.div
                  key={service.title}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white p-6 rounded-xl shadow-sm hover:shadow-lg transition-shadow"
                >
                  <div className="w-12 h-12 bg-teal/10 rounded-xl flex items-center justify-center mb-4">
                    <IconComponent className="w-6 h-6 text-teal" />
                  </div>
                  <h3 className="font-bold text-navy mb-4">{service.title}</h3>
                  <ul className="space-y-2">
                    {service.items.map((item, i) => (
                      <li key={i} className="flex items-start text-sm text-mid-grey">
                        <CheckCircle2 className="w-4 h-4 text-teal mr-2 flex-shrink-0 mt-0.5" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Project Types */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <h3 className="font-playfair text-2xl text-navy mb-8 text-center">Project Types</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {projectTypes.map((type) => (
              <motion.div 
                key={type}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                className="bg-light-grey p-4 rounded-lg text-center hover:bg-teal hover:text-white transition-colors cursor-pointer"
              >
                <span className="font-medium text-sm">{type}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Engagement Models */}
      <section className="py-20 md:py-28 bg-light-grey" data-testid="engagement-models-section">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center mb-12"
          >
            <motion.span variants={fadeInUp} className="text-teal font-medium text-sm uppercase tracking-wider">
              How We Partner
            </motion.span>
            <motion.h2 variants={fadeInUp} className="font-playfair text-3xl md:text-4xl text-navy mt-4">
              Engagement Models
            </motion.h2>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {engagementModels.map((model, index) => {
              const IconComponent = model.icon;
              return (
                <motion.div
                  key={model.title}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white p-6 rounded-xl shadow-sm hover:shadow-lg transition-shadow"
                  data-testid={`engagement-model-${index}`}
                >
                  <div className="w-12 h-12 bg-teal/10 rounded-xl flex items-center justify-center mb-4">
                    <IconComponent className="w-6 h-6 text-teal" />
                  </div>
                  <h4 className="font-bold text-navy mb-2">{model.title}</h4>
                  <p className="text-mid-grey text-sm">{model.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Industries We Serve */}
      <section className="py-16 bg-white" data-testid="industries-we-serve-section">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center mb-10"
          >
            <motion.span variants={fadeInUp} className="text-teal font-medium text-sm uppercase tracking-wider">
              Sector Expertise
            </motion.span>
            <motion.h3 variants={fadeInUp} className="font-playfair text-2xl md:text-3xl text-navy mt-4">
              Industries We Serve
            </motion.h3>
          </motion.div>
          <div className="flex flex-wrap justify-center gap-3">
            {industriesWeServe.map((industry) => (
              <span
                key={industry}
                className="px-5 py-2.5 bg-light-grey rounded-full text-navy font-medium text-sm hover:bg-teal hover:text-white transition-colors cursor-pointer"
              >
                {industry}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Our Approach */}
      <section className="py-20 md:py-28 bg-light-grey">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center mb-12"
          >
            <motion.span variants={fadeInUp} className="text-teal font-medium text-sm uppercase tracking-wider">
              How We Work
            </motion.span>
            <motion.h2 variants={fadeInUp} className="font-playfair text-3xl md:text-4xl text-navy mt-4">
              Our Approach
            </motion.h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {approach.map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white p-6 rounded-xl text-center"
              >
                <div className="w-12 h-12 bg-teal text-white rounded-full flex items-center justify-center mx-auto mb-4 font-bold">
                  {index + 1}
                </div>
                <h4 className="font-bold text-navy mb-2">{item.title}</h4>
                <p className="text-mid-grey text-sm">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Leadership */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center mb-12"
          >
            <motion.span variants={fadeInUp} className="text-teal font-medium text-sm uppercase tracking-wider">
              Meet Our Expert
            </motion.span>
            <motion.h2 variants={fadeInUp} className="font-playfair text-3xl md:text-4xl text-navy mt-4">
              Leadership
            </motion.h2>
          </motion.div>

          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-light-grey rounded-2xl p-8"
            >
              <div className="flex flex-col md:flex-row gap-8">
                <div className="md:w-1/3">
                  <div className="w-32 h-32 bg-teal/20 rounded-full flex items-center justify-center mx-auto md:mx-0">
                    <Users className="w-16 h-16 text-teal" />
                  </div>
                </div>
                <div className="md:w-2/3">
                  <h3 className="font-bold text-2xl text-navy">{leaderProfile.name}</h3>
                  <p className="text-teal font-medium mb-2">{leaderProfile.title}</p>
                  <div className="flex flex-wrap gap-2 mb-4">
                    {leaderProfile.credentials.map((cred, i) => (
                      <span key={i} className="text-xs bg-white px-3 py-1 rounded-full text-mid-grey">{cred}</span>
                    ))}
                  </div>
                  <p className="text-mid-grey mb-4">{leaderProfile.summary}</p>
                  
                  <button
                    onClick={() => setExpandedProfile(!expandedProfile)}
                    className="flex items-center text-teal font-medium hover:text-navy transition-colors"
                  >
                    {expandedProfile ? 'Show Less' : 'View Full Profile'}
                    {expandedProfile ? <ChevronUp className="w-4 h-4 ml-1" /> : <ChevronDown className="w-4 h-4 ml-1" />}
                  </button>

                  {expandedProfile && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      className="mt-6 space-y-6"
                    >
                      <div>
                        <h4 className="font-bold text-navy mb-2">Professional Experience</h4>
                        <ul className="space-y-1">
                          {leaderProfile.experience.map((exp, i) => (
                            <li key={i} className="text-sm text-mid-grey flex items-start">
                              <CheckCircle2 className="w-4 h-4 text-teal mr-2 flex-shrink-0 mt-0.5" />
                              {exp}
                            </li>
                          ))}
                        </ul>
                      </div>

                      <div>
                        <h4 className="font-bold text-navy mb-2">Core Expertise</h4>
                        <div className="grid grid-cols-2 gap-2">
                          {leaderProfile.expertise.map((exp, i) => (
                            <span key={i} className="text-xs bg-white px-3 py-2 rounded-lg text-mid-grey">{exp}</span>
                          ))}
                        </div>
                      </div>

                      <div>
                        <h4 className="font-bold text-navy mb-2">Key Strengths</h4>
                        <ul className="space-y-1">
                          {leaderProfile.strengths.map((str, i) => (
                            <li key={i} className="text-sm text-mid-grey flex items-start">
                              <CheckCircle2 className="w-4 h-4 text-teal mr-2 flex-shrink-0 mt-0.5" />
                              {str}
                            </li>
                          ))}
                        </ul>
                      </div>

                      <div>
                        <h4 className="font-bold text-navy mb-2">Tools & Methodologies</h4>
                        <div className="flex flex-wrap gap-2">
                          {leaderProfile.tools.map((tool, i) => (
                            <span key={i} className="text-xs bg-teal/10 text-teal px-3 py-1 rounded-full">{tool}</span>
                          ))}
                        </div>
                      </div>

                      <div>
                        <h4 className="font-bold text-navy mb-2">Audit & Regulatory Exposure</h4>
                        <div className="flex flex-wrap gap-2">
                          {leaderProfile.audits.map((audit, i) => (
                            <span key={i} className="text-xs bg-white px-3 py-1 rounded-full text-mid-grey border border-gray-200">{audit}</span>
                          ))}
                        </div>
                      </div>
                    </motion.div>
                  )}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Case Study Results */}
      <section className="py-16 bg-navy">
        <div className="max-w-7xl mx-auto px-6">
          <h3 className="text-white text-2xl font-bold text-center mb-8">Case Study Results</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {caseStudyResults.map((result, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center"
              >
                <div className="text-4xl md:text-5xl font-bold text-white mb-2">{result.value}</div>
                <p className="text-white/70 text-sm">{result.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Deliverables */}
      <section className="py-16 bg-light-grey">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h3 className="font-playfair text-2xl text-navy mb-4">Deliverables</h3>
          <p className="text-mid-grey mb-6">Every project concludes with comprehensive handover dossiers including all documentation, as-built drawings, qualification documents, and operational guidelines.</p>
          <div className="inline-flex items-center bg-white px-6 py-3 rounded-full shadow-sm">
            <FileCheck className="w-5 h-5 text-teal mr-2" />
            <span className="font-medium text-navy">Complete Handover Dossiers</span>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-28 bg-teal">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="font-playfair text-4xl md:text-5xl text-white mb-6">
              Discuss Your Project
            </h2>
            <p className="text-white/80 text-lg mb-8 max-w-2xl mx-auto">
              Whether you're planning a greenfield facility, upgrading an existing plant, or need audit support, our experts are ready to help.
            </p>
            <Link 
              to={`/${locale}/contact`}
              className="btn-primary inline-flex items-center"
              data-testid="pharma-cta-contact"
            >
              Start a Conversation <ArrowRight className="w-4 h-4 ml-2" />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default PharmaEngineeringPage;
